import { Badge, Flex, Text } from '@chakra-ui/react';
import CustomFieldBadge from '../../../components/customfield-badge/CustomFieldBadge';
import CustomFieldLabel from '../../../components/customfield-label/CustomFieldLabel';
import { NavigateText } from '../../../components/navigate-text/NavigateText';
import PracticeStatusBadge from '../../../components/practice-status-badge/PracticeStatusBadge';
import { ColumnConfig } from '../../../hooks/useColumnManagement';
import { OfficeHour, Practice } from '../../../types';
import { CustomField } from '../../../types/custom-fields/types';
import {
  getCustomFieldValue,
  getLabImagingDisplay,
  getLabImagingValue,
} from '../../../utils/customFieldUtils';
import { isExternalPractice } from '../../../utils/externalEntity';
import { constructPracticeAddress } from '../../../utils/practiceUtils';
import ExternalAffordance from '../../../components/external-indicator/ExternalAffordance';
import { timeZoneMapping } from '../../../utils/timeZoneMapping';
import { formatTimeWithCorrectTimezone } from '../../../utils/timezoneDisplay';

interface Column<T> {
  header: string; // Column header text
  accessor: keyof T; // Key to access data
  isCustomRender?: boolean; // If custom rendering is needed
  render?: (data: T) => React.ReactNode; // Custom render function
  width?: string;
}

// Helper functions from PracticeStatusBox.tsx
const normalizeTimeString = (
  time: string | null | undefined,
  isCloseTime?: boolean
): string | undefined => {
  if (!time) return undefined;

  // Handle 12-hour format with AM/PM (e.g., "2:30 PM", "12:00 AM")
  if (time.includes('AM') || time.includes('PM')) {
    const [timePart, meridiem] = time
      .toUpperCase()
      .split(/(?=AM|PM)/);
    const [h, m] = timePart.split(':');
    const hour = Number(h);
    if (isNaN(hour) || isNaN(Number(m))) return undefined;

    let hour24 = hour;
    if (meridiem === 'PM' && hour !== 12) {
      hour24 = hour + 12;
    } else if (meridiem === 'AM' && hour === 12) {
      hour24 = 0;
    }

    return `${hour24.toString().padStart(2, '0')}:${m}`;
  }

  // Already in "HH:mm" format
  if (/^\d{2}:\d{2}$/.test(time)) return time;

  // "H:mm" or "HH:mm"
  if (/^\d{1,2}:\d{2}$/.test(time)) {
    const [h, m] = time.split(':');
    const hour = Number(h);
    if (isNaN(hour) || isNaN(Number(m))) return undefined;
    // For close times, treat 1-11 as PM, 12 as noon, 0 as midnight
    if (isCloseTime) {
      if (hour === 0) return `00:${m}`;
      if (hour === 12) return `12:${m}`; // noon
      if (hour >= 1 && hour <= 11)
        return `${(hour + 12).toString().padStart(2, '0')}:${m}`;
      // 13-23 are already PM
      return `${hour.toString().padStart(2, '0')}:${m}`;
    } else {
      // For open times, treat 12 as noon, 0 as midnight, 1-11 as AM
      if (hour === 0) return `00:${m}`;
      if (hour === 12) return `12:${m}`; // noon
      return `${hour.toString().padStart(2, '0')}:${m}`;
    }
  }
  return undefined;
};

const parseTime = (time: string, reference: Date) => {
  if (!time) return new Date(reference);

  let hour24: number;
  let minute: number;

  // Handle 12-hour format (e.g., "2:30 PM")
  if (time.includes('AM') || time.includes('PM')) {
    const [timePart, meridiem] = time
      .toUpperCase()
      .split(/(?=AM|PM)/);
    const [h, m] = timePart.split(':').map(Number);

    if (isNaN(h) || isNaN(m)) return new Date(reference);

    hour24 = h;
    if (meridiem === 'PM' && h !== 12) {
      hour24 = h + 12;
    } else if (meridiem === 'AM' && h === 12) {
      hour24 = 0;
    }
    minute = m;
  } else {
    // Handle 24-hour format (e.g., "14:30")
    const [h, m] = time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return new Date(reference);
    hour24 = h;
    minute = m;
  }

  const date = new Date(reference);
  date.setHours(hour24, minute, 0, 0);
  return date;
};

const getPracticeStatus = (
  timezone: string,
  rawOfficeHours: OfficeHour[]
): { status: string; badgeColor: string } => {
  const IanaTimeZone = timeZoneMapping[timezone] || 'UTC';
  const now = new Date();
  // Get current date/time in the practice's timezone
  const currentDate = new Date(
    now.toLocaleString('en-US', { timeZone: IanaTimeZone })
  );
  const todayIndex = currentDate.getDay(); // 0 = Sunday

  // Filter today's blocks (backend: 1=Sunday, 2=Monday, ..., 7=Saturday)
  const todayBlocks = rawOfficeHours
    .filter(
      (item) =>
        item.day_of_week === todayIndex + 1 &&
        !item.closed &&
        item.open_time &&
        item.close_time
    )
    .sort((a, b) => {
      const aOpen = normalizeTimeString(a.open_time) ?? '';
      const bOpen = normalizeTimeString(b.open_time) ?? '';
      return aOpen.localeCompare(bOpen);
    });

  // Defensive: skip if no valid blocks
  if (!todayBlocks.length) {
    return { status: 'CLOSED', badgeColor: 'red' };
  }

  // Check each block for open/closing soon
  for (const block of todayBlocks) {
    const openNorm = normalizeTimeString(block.open_time);
    const closeNorm = normalizeTimeString(block.close_time, true);
    if (!openNorm || !closeNorm) continue;
    const start = parseTime(openNorm, currentDate);
    const end = parseTime(closeNorm, currentDate);

    // If end is before start (shouldn't happen with normalization, but just in case), treat as next day
    if (end <= start) end.setDate(end.getDate() + 1);

    if (currentDate >= start && currentDate < end) {
      const minutesLeft = Math.floor(
        (end.getTime() - currentDate.getTime()) / 60000
      );
      if (minutesLeft <= 30)
        return { status: 'CLOSING SOON', badgeColor: 'orange' };
      return { status: 'OPEN', badgeColor: 'green' };
    }
  }

  // If two blocks, check for lunch gap
  if (todayBlocks.length === 2) {
    const endOfMorningNorm = normalizeTimeString(
      todayBlocks[0].close_time,
      true
    );
    const startOfAfternoonNorm = normalizeTimeString(
      todayBlocks[1].open_time
    );

    if (endOfMorningNorm && startOfAfternoonNorm) {
      const endOfMorning = parseTime(endOfMorningNorm, currentDate);
      const startOfAfternoon = parseTime(
        startOfAfternoonNorm,
        currentDate
      );

      // Check if currently during lunch
      if (
        currentDate >= endOfMorning &&
        currentDate < startOfAfternoon
      ) {
        return { status: 'LUNCH', badgeColor: 'yellow' };
      }

      // Check if lunch is approaching (within 30 minutes)
      if (currentDate < endOfMorning) {
        const minutesUntilLunch = Math.floor(
          (endOfMorning.getTime() - currentDate.getTime()) / 60000
        );

        if (minutesUntilLunch <= 30) {
          return {
            status: 'LUNCH APPROACHING',
            badgeColor: 'orange',
          };
        }
      }

      // Check if opening soon (within 30 minutes of first block opening)
      const firstBlockOpen = parseTime(
        normalizeTimeString(todayBlocks[0].open_time)!,
        currentDate
      );
      if (currentDate < firstBlockOpen) {
        const minutesUntilOpen = Math.floor(
          (firstBlockOpen.getTime() - currentDate.getTime()) / 60000
        );

        if (minutesUntilOpen <= 30) {
          return { status: 'OPENING SOON', badgeColor: 'blue' };
        }
      }
    }
  }

  // Otherwise, closed
  return { status: 'CLOSED', badgeColor: 'red' };
};

// Default column configuration for column management
export const getDefaultPracticeColumns = (): ColumnConfig<Practice>[] => [
  {
    id: 'name',
    header: 'Name',
    accessor: 'name',
    isVisible: true,
    order: 0,
    width: '250px',
    isCustomRender: true,
  },
  {
    id: 'address',
    header: 'Address',
    accessor: 'address1',
    isVisible: true,
    order: 1,
    isCustomRender: true,
  },
  {
    id: 'phone',
    header: 'Main phone number',
    accessor: 'managerPhone',
    isVisible: false,
    order: 2,
    isCustomRender: true,
  },
  {
    id: 'market',
    header: 'Market',
    accessor: 'market',
    isVisible: true,
    order: 3,
    isCustomRender: true,
  },
  {
    id: 'local-time',
    header: 'Local time',
    accessor: 'office_hours',
    isVisible: true,
    order: 4,
    isCustomRender: true,
  },
  {
    id: 'providers',
    header: 'Providers',
    accessor: 'providerCount',
    isVisible: true,
    order: 5,
    isCustomRender: true,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'active',
    isVisible: true,
    order: 6,
    isCustomRender: true,
  },
  // Additional columns that can be shown/hidden
  {
    id: 'flu-shots',
    header: 'Flu Shots',
    accessor: 'fluShots',
    isVisible: true,
    order: 7,
    isCustomRender: true,
  },
  {
    id: 'treats-all-ages',
    header: 'Treats All ages treated',
    accessor: 'treatsAllAges',
    isVisible: true,
    order: 8,
    isCustomRender: true,
  },
  {
    id: 'external-id',
    header: 'External ID',
    accessor: 'externalId',
    isVisible: false,
    order: 9,
  },
  {
    id: 'tablespace',
    header: 'Tablespace',
    accessor: 'externalTablespace',
    isVisible: false,
    order: 10,
  },
  {
    id: 'timezone',
    header: 'Timezone',
    accessor: 'timezone',
    isVisible: false,
    order: 11,
  },
  {
    id: 'manager-email',
    header: 'Manager Email',
    accessor: 'managerEmail',
    isVisible: false,
    order: 12,
  },
  {
    id: 'manager-name',
    header: 'Manager Name',
    accessor: 'managerName',
    isVisible: false,
    order: 13,
  },
  {
    id: 'start-date',
    header: 'Start Date',
    accessor: 'startDate',
    isVisible: false,
    order: 14,
  },
  {
    id: 'end-date',
    header: 'End Date',
    accessor: 'endDate',
    isVisible: false,
    order: 15,
  },
  // Custom Fields - Contact Information
  {
    id: 'backline',
    header: 'Backline',
    accessor: 'customFields',
    isVisible: false,
    order: 16,
    isCustomRender: true,
  },
  {
    id: 'manager-line',
    header: 'Manager Line',
    accessor: 'customFields',
    isVisible: false,
    order: 17,
    isCustomRender: true,
  },
  {
    id: 'after-hours',
    header: 'After Hours',
    accessor: 'customFields',
    isVisible: false,
    order: 18,
    isCustomRender: true,
  },
  {
    id: 'phone-custom',
    header: 'Phone (Custom)',
    accessor: 'customFields',
    isVisible: false,
    order: 19,
    isCustomRender: true,
  },
  {
    id: 'fax-custom',
    header: 'Fax (Custom)',
    accessor: 'customFields',
    isVisible: false,
    order: 20,
    isCustomRender: true,
  },
  {
    id: 'medical-records-number',
    header: 'Medical Records Number',
    accessor: 'customFields',
    isVisible: false,
    order: 21,
    isCustomRender: true,
  },
  {
    id: 'directions',
    header: 'Directions',
    accessor: 'customFields',
    isVisible: false,
    order: 22,
    isCustomRender: true,
  },
  // Custom Fields - Clinical Information
  {
    id: 'clinic-npi',
    header: 'Clinic NPI',
    accessor: 'customFields',
    isVisible: false,
    order: 23,
    isCustomRender: true,
  },
  {
    id: 'group-tid',
    header: 'Group Tax ID',
    accessor: 'customFields',
    isVisible: false,
    order: 24,
    isCustomRender: true,
  },
  {
    id: 'ekg',
    header: 'EKG',
    accessor: 'customFields',
    isVisible: false,
    order: 25,
    isCustomRender: true,
  },
  {
    id: 'screen-for-covid',
    header: 'Screen for COVID',
    accessor: 'customFields',
    isVisible: false,
    order: 27,
    isCustomRender: true,
  },
  {
    id: 'labs-drawn-hours',
    header: 'Labs Drawn Hours',
    accessor: 'customFields',
    isVisible: false,
    order: 28,
    isCustomRender: true,
  },
  {
    id: 'imaging-available-hours',
    header: 'Imaging Available Hours',
    accessor: 'customFields',
    isVisible: false,
    order: 29,
    isCustomRender: true,
  },
  // Custom Fields - Staff Information
  {
    id: 'clinical-staff',
    header: 'Clinical Staff',
    accessor: 'customFields',
    isVisible: false,
    order: 30,
    isCustomRender: true,
  },
  {
    id: 'front-staff',
    header: 'Front Staff',
    accessor: 'customFields',
    isVisible: false,
    order: 31,
    isCustomRender: true,
  },
  {
    id: 'director',
    header: 'Director',
    accessor: 'customFields',
    isVisible: false,
    order: 32,
    isCustomRender: true,
  },
];

// Function to convert ColumnConfig to Column format
export const convertToColumns = (
  configs: ColumnConfig<Practice>[]
): Column<Practice>[] => {
  return configs.map((config) => ({
    header: config.header,
    accessor: config.accessor as keyof Practice,
    isCustomRender: config.isCustomRender,
    width: config.width,
    render: config.render,
  }));
};

// Render functions for each column
const renderFunctions = {
  name: (row: Practice) => (
    <Flex align="center" gap="0.4rem" wrap="wrap">
      <NavigateText name={row.name} id={row.id} resource="practice" />
      {isExternalPractice(row) && (
        <ExternalAffordance entityType="practice" size="sm" />
      )}
    </Flex>
  ),
  address: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {constructPracticeAddress(row)}
    </Text>
  ),
  phone: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.managerPhone || '-'}
    </Text>
  ),
  market: (row: Practice) =>
    row.market && row.marketId ? (
      <NavigateText
        name={row.market}
        id={row.marketId}
        resource="market"
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
        -
      </Text>
    ),
  'local-time': (row: Practice) => {
    // Get current time in practice's timezone
    const timezone = row.timezone || 'CST';
    const IanaTimeZone =
      timeZoneMapping[timezone] || 'America/Chicago';

    const timeInfo = formatTimeWithCorrectTimezone(
      timezone,
      IanaTimeZone
    );
    const timeString = timeInfo.timeString;

    // Get practice status using the same logic as PracticeStatusBox
    const statusInfo = row.office_hours?.length
      ? getPracticeStatus(timezone, row.office_hours)
      : { status: 'CLOSED', badgeColor: 'red' };

    // Format status text like PracticeStatusBox
    const statusText =
      statusInfo.status === 'LUNCH'
        ? 'Lobby Closed'
        : statusInfo.status === 'LUNCH APPROACHING'
        ? 'Lobby Closing'
        : statusInfo.status === 'OPENING SOON'
        ? 'Opening Soon'
        : statusInfo.status === 'CLOSING SOON'
        ? 'Closing Soon'
        : statusInfo.status;

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <Text fontSize="0.75rem" lineHeight="1rem">
          {timeString} {timeInfo.timezoneAbbreviation}
        </Text>
        <Badge
          variant="solid"
          colorScheme={statusInfo.badgeColor}
          fontSize="0.625rem"
          px="0.5rem"
          py="0.125rem"
          borderRadius="0.375rem"
          textTransform="uppercase"
          fontWeight="600"
        >
          {statusText}
        </Badge>
      </div>
    );
  },
  providers: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.providerCount || 0}
    </Text>
  ),
  status: (row: Practice) =>
    row?.active !== undefined && !isExternalPractice(row) ? (
      <Flex width="20rem" wrap="wrap">
        <PracticeStatusBadge
          active={row.active}
          practiceOnPac={row.practiceOnPac}
        />
      </Flex>
    ) : null,
  'external-id': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {(row as unknown as { externalId?: string }).externalId || '-'}
    </Text>
  ),
  tablespace: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {(row as unknown as { externalTablespace?: string })
        .externalTablespace || '-'}
    </Text>
  ),
  'flu-shots': (row: Practice) => (
    <CustomFieldLabel
      customField=""
      fieldValue={row.fluShots ? 'Available' : 'Not Available'}
      fieldValueBgColor={
        row.fluShots ? 'background.teal100' : 'background.red100'
      }
      hideCustomFieldLabel={true}
    />
  ),
  'treats-all-ages': (row: Practice) => (
    <CustomFieldLabel
      customField=""
      fieldValue={row.treatsAllAges ? 'Yes' : 'No'}
      fieldValueBgColor={
        row.treatsAllAges ? 'background.teal100' : 'background.red100'
      }
      hideCustomFieldLabel={true}
    />
  ),
  timezone: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.timezone || '-'}
    </Text>
  ),
  'manager-email': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.managerEmail || '-'}
    </Text>
  ),
  'manager-name': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.managerName || '-'}
    </Text>
  ),
  'start-date': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.startDate || '-'}
    </Text>
  ),
  'end-date': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.endDate || '-'}
    </Text>
  ),
  active: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {row.active ? 'Yes' : 'No'}
    </Text>
  ),
  // Custom Fields - Contact Information
  backline: (row: Practice) => {
    const value = getCustomFieldValue(row.customFields, 'Backline');
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="#FED7D7"
        titleColor="#9B2C2C"
        valueColor="#9B2C2C"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  'manager-line': (row: Practice) => {
    const value = getCustomFieldValue(
      row.customFields,
      'Manager-Line'
    );
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="background.orange200"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  'after-hours': (row: Practice) => {
    const value = getCustomFieldValue(
      row.customFields,
      'After-Hours'
    );
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="background.cyan100"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  'phone-custom': (row: Practice) => {
    const value = getCustomFieldValue(row.customFields, 'Phone');
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="background.gray50"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  'fax-custom': (row: Practice) => {
    const value = getCustomFieldValue(row.customFields, 'Fax');
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="background.gray50"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  'medical-records-number': (row: Practice) => {
    const value = getCustomFieldValue(
      row.customFields,
      'Medical-Records-Number'
    );
    return value ? (
      <CustomFieldBadge
        customField=""
        fieldValue={value}
        bgColor="background.gray50"
        showCustomFieldLabel={false}
      />
    ) : (
      <Text fontSize="0.75rem" lineHeight="1rem">
        -
      </Text>
    );
  },
  directions: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {getCustomFieldValue(row.customFields, 'Directions') || '-'}
    </Text>
  ),
  // Custom Fields - Clinical Information
  'clinic-npi': (row: Practice) => (
    <CustomFieldLabel
      customField=""
      fieldValue={
        getCustomFieldValue(row.customFields, 'Clinic NPI') || '-'
      }
      fieldValueBgColor="background.gray100"
      hideCustomFieldLabel={true}
    />
  ),
  'group-tid': (row: Practice) => (
    <CustomFieldLabel
      customField=""
      fieldValue={
        getCustomFieldValue(row.customFields, 'Group-TID') || '-'
      }
      fieldValueBgColor="background.gray100"
      hideCustomFieldLabel={true}
    />
  ),
  ekg: (row: Practice) => {
    const val = getCustomFieldValue(row.customFields, 'Ekg');
    return (
      <CustomFieldLabel
        customField=""
        fieldValue={val || '-'}
        fieldValueBgColor={
          val?.toLowerCase() === 'yes'
            ? 'background.teal100'
            : val?.toLowerCase() === 'no'
            ? 'background.red100'
            : 'background.gray100'
        }
        hideCustomFieldLabel={true}
      />
    );
  },
  'screen-for-covid': (row: Practice) => {
    const fieldValue = getCustomFieldValue(
      row.customFields,
      'Screen-For-Covid-When-Scheduling'
    );
    return (
      <CustomFieldLabel
        customField=""
        fieldValue={fieldValue || '-'}
        fieldValueBgColor={
          !fieldValue
            ? 'background.gray100'
            : fieldValue.toLowerCase() === 'yes' ||
              fieldValue.toLowerCase() === 'true'
            ? 'background.teal100'
            : fieldValue.toLowerCase() === 'no' ||
              fieldValue.toLowerCase() === 'false'
            ? 'background.red100'
            : 'background.gray100'
        }
        hideCustomFieldLabel={true}
      />
    );
  },
  'labs-drawn-hours': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {
        getLabImagingDisplay(
          getLabImagingValue(row.customFields, 'Labs-Drawn-Hours')
        ).displayText
      }
    </Text>
  ),
  'imaging-available-hours': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {
        getLabImagingDisplay(
          getLabImagingValue(
            row.customFields,
            'Imaging-Available-Hours'
          )
        ).displayText
      }
    </Text>
  ),
  // Custom Fields - Staff Information
  'clinical-staff': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {getCustomFieldValue(row.customFields, 'Clinical-Staff') || '-'}
    </Text>
  ),
  'front-staff': (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {getCustomFieldValue(row.customFields, 'Front-Staff') || '-'}
    </Text>
  ),
  director: (row: Practice) => (
    <Text fontSize="0.75rem" lineHeight="1rem">
      {getCustomFieldValue(row.customFields, 'Director') || '-'}
    </Text>
  ),
};

// Function to generate custom field columns dynamically
export const generateCustomFieldColumns = (
  customFields: CustomField[]
): ColumnConfig<Practice>[] => {
  return customFields.map((field, index) => {
    const fieldId = `custom-${field.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')}`;

    // Make Backline visible by default
    const isVisible = field.name === 'Backline';

    return {
      id: fieldId,
      header: field.name,
      accessor: 'customFields' as keyof Practice,
      isVisible,
      order: isVisible ? 2 : 50 + index, // Backline gets order 2, others start at 50+
      isCustomRender: true,
    };
  });
};

// Function to generate custom field render functions dynamically
export const generateCustomFieldRenderFunctions = (
  customFields: CustomField[]
) => {
  const customRenderFunctions: Record<
    string,
    (row: Practice) => React.ReactNode
  > = {};

  customFields.forEach((field) => {
    const fieldId = `custom-${field.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')}`;

    // Determine the appropriate component based on field type and allowed values
    if (field.allowed_values && field.allowed_values.length > 0) {
      // Field with predefined values - use CustomFieldLabel with conditional styling
      customRenderFunctions[fieldId] = (row: Practice) => {
        const value = getCustomFieldValue(
          row.customFields,
          field.name
        );
        const allowedValues =
          field.allowed_values?.map((v) => v.value.toLowerCase()) ||
          [];

        // Determine background color based on value
        let bgColor = 'background.gray100';
        if (value) {
          const lowerValue = value.toLowerCase();
          if (allowedValues.includes('yes') && lowerValue === 'yes') {
            bgColor = 'background.teal100';
          } else if (
            allowedValues.includes('no') &&
            lowerValue === 'no'
          ) {
            bgColor = 'background.red100';
          } else if (
            allowedValues.includes('available') &&
            lowerValue === 'available'
          ) {
            bgColor = 'background.teal100';
          } else if (
            allowedValues.includes('not available') &&
            lowerValue === 'not available'
          ) {
            bgColor = 'background.red100';
          } else if (
            allowedValues.includes('true') &&
            lowerValue === 'true'
          ) {
            bgColor = 'background.teal100';
          } else if (
            allowedValues.includes('false') &&
            lowerValue === 'false'
          ) {
            bgColor = 'background.red100';
          }
        }

        return (
          <CustomFieldLabel
            customField=""
            fieldValue={value || '-'}
            fieldValueBgColor={bgColor}
            hideCustomFieldLabel={true}
          />
        );
      };
    } else if (
      field.field_type === 'text' ||
      field.field_type === 'textarea'
    ) {
      // Text fields - use CustomFieldBadge for better display
      customRenderFunctions[fieldId] = (row: Practice) => {
        const value = getCustomFieldValue(
          row.customFields,
          field.name
        );
        return value ? (
          <CustomFieldBadge
            customField=""
            fieldValue={value}
            bgColor="background.gray50"
            showCustomFieldLabel={false}
          />
        ) : (
          <Text fontSize="0.75rem" lineHeight="1rem">
            -
          </Text>
        );
      };
    } else {
      // Default - simple text display
      customRenderFunctions[fieldId] = (row: Practice) => (
        <Text fontSize="0.75rem" lineHeight="1rem">
          {getCustomFieldValue(row.customFields, field.name) || '-'}
        </Text>
      );
    }
  });

  return customRenderFunctions;
};

// Update the default columns with render functions
export const getDefaultPracticeColumnsWithRenders =
  (): ColumnConfig<Practice>[] => {
    return getDefaultPracticeColumns().map((config) => ({
      ...config,
      render:
        renderFunctions[config.id as keyof typeof renderFunctions],
    }));
  };

// Function to get all columns (root + custom fields) with render functions
export const getAllPracticeColumnsWithRenders = (
  customFields: CustomField[]
): ColumnConfig<Practice>[] => {
  const rootColumns = getDefaultPracticeColumnsWithRenders();
  const customFieldColumns = generateCustomFieldColumns(customFields);
  const customFieldRenderFunctions =
    generateCustomFieldRenderFunctions(customFields);

  // Combine all columns
  const allColumns = [...rootColumns, ...customFieldColumns];

  // Add custom field render functions to the main render functions
  const allRenderFunctions = {
    ...renderFunctions,
    ...customFieldRenderFunctions,
  };

  return allColumns.map((config) => ({
    ...config,
    render:
      allRenderFunctions[
        config.id as keyof typeof allRenderFunctions
      ],
  }));
};

export const practiceColumns: Column<Practice>[] = [
  {
    header: 'Name',
    accessor: 'name',
    isCustomRender: true,
    width: '250px',
    render: (row: Practice) => (
      <NavigateText name={row.name} id={row.id} resource="practice" />
    ),
  },
  {
    header: 'Address',
    accessor: 'address1',
    isCustomRender: true,
    render: (row: Practice) => (
      <Text fontSize="0.75rem" lineHeight="1rem">
        {constructPracticeAddress(row)}
      </Text>
    ),
  },
  {
    header: 'Main phone number',
    accessor: 'managerPhone',
    isCustomRender: true,
    render: (row: Practice) => (
      <Text fontSize="0.75rem" lineHeight="1rem">
        {row.managerPhone || '-'}
      </Text>
    ),
  },
  {
    header: 'Market',
    accessor: 'market',
    isCustomRender: true,
    render: (row: Practice) =>
      row.market && row.marketId ? (
        <NavigateText
          name={row.market}
          id={row.marketId}
          resource="market"
        />
      ) : (
        <Text fontSize="0.75rem" lineHeight="1rem" color="gray.500">
          -
        </Text>
      ),
  },
  {
    header: 'Local time',
    accessor: 'office_hours',
    isCustomRender: true,
    render: (row: Practice) => {
      // Get current time in practice's timezone
      const timezone = row.timezone || 'CST';
      const IanaTimeZone =
        timeZoneMapping[timezone] || 'America/Chicago';

      const timeInfo = formatTimeWithCorrectTimezone(
        timezone,
        IanaTimeZone
      );
      const timeString = timeInfo.timeString;

      // Get practice status using the same logic as PracticeStatusBox
      const statusInfo = row.office_hours?.length
        ? getPracticeStatus(timezone, row.office_hours)
        : { status: 'CLOSED', badgeColor: 'red' };

      // Format status text like PracticeStatusBox
      const statusText =
        statusInfo.status === 'LUNCH'
          ? 'Lobby Closed'
          : statusInfo.status === 'LUNCH APPROACHING'
          ? 'Lobby Closing'
          : statusInfo.status === 'OPENING SOON'
          ? 'Opening Soon'
          : statusInfo.status === 'CLOSING SOON'
          ? 'Closing Soon'
          : statusInfo.status;

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <Text fontSize="0.75rem" lineHeight="1rem">
            {timeString} {timeInfo.timezoneAbbreviation}
          </Text>
          <Badge
            variant="solid"
            colorScheme={statusInfo.badgeColor}
            fontSize="0.625rem"
            px="0.5rem"
            py="0.125rem"
            borderRadius="0.375rem"
            textTransform="uppercase"
            fontWeight="600"
          >
            {statusText}
          </Badge>
        </div>
      );
    },
  },
  {
    header: 'Providers',
    accessor: 'providerCount',
    isCustomRender: true,
    render: (row: Practice) => (
      <Text fontSize="0.75rem" lineHeight="1rem">
        {row.providerCount || 0}
      </Text>
    ),
  },
];

export default practiceColumns;
